'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VehicleDetails {
  year: string;
  make: string;
  model: string;
  mileage: string;
  partDescription: string;
}

interface VehicleInputFormProps {
  onSearch: (details: VehicleDetails) => void;
  isLoading?: boolean;
}

const VehicleInputForm = ({ onSearch, isLoading = false }: VehicleInputFormProps) => {
  const [details, setDetails] = useState<VehicleDetails>({
    year: '',
    make: '',
    model: '',
    mileage: '',
    partDescription: ''
  });

  const [errors, setErrors] = useState<Partial<VehicleDetails>>({});

  const years = Array.from({ length: 30 }, (_, i) => (new Date().getFullYear() - i).toString());
  const makes = ['BMW', 'Lexus', 'Audi', 'Mercedes', 'Toyota', 'Honda', 'Acura'];
  
  const bmwModels = ['3 Series', '5 Series', '7 Series', 'X3', 'X5', 'X7', 'M3', 'M5'];
  const lexusModels = ['ES', 'IS', 'GS', 'LS', 'RX', 'NX', 'GX', 'LX'];
  const audiModels = ['A3', 'A4', 'A6', 'A8', 'Q3', 'Q5', 'Q7', 'Q8'];
  const mercedesModels = ['C-Class', 'E-Class', 'S-Class', 'GLA', 'GLC', 'GLE', 'GLS'];
  const toyotaModels = ['Camry', 'Corolla', 'RAV4', 'Highlander', 'Prius', 'Tacoma', 'Tundra', 'Sienna'];
  const hondaModels = ['Civic', 'Accord', 'CR-V', 'Pilot', 'Odyssey', 'Fit', 'HR-V', 'Ridgeline'];
  const acuraModels = ['MDX', 'RDX', 'TLX', 'Integra', 'ILX', 'TSX', 'RLX', 'RSX'];

  const getModels = () => {
    if (details.make === 'BMW') return bmwModels;
    if (details.make === 'Lexus') return lexusModels;
    if (details.make === 'Audi') return audiModels;
    if (details.make === 'Mercedes') return mercedesModels;
    if (details.make === 'Toyota') return toyotaModels;
    if (details.make === 'Honda') return hondaModels;
    if (details.make === 'Acura') return acuraModels;
    return [];
  };

  const handleChange = (field: keyof VehicleDetails, value: string) => {
    setDetails(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    if (field === 'make') {
      setDetails(prev => ({ ...prev, model: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<VehicleDetails> = {};
    
    if (!details.year) newErrors.year = 'Year is required';
    if (!details.make) newErrors.make = 'Make is required';
    if (!details.model) newErrors.model = 'Model is required';
    if (!details.partDescription) newErrors.partDescription = 'Part description is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSearch(details);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="year" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Year <span className="text-error">*</span>
          </label>
          <div className="relative">
            <Icon 
              name="CalendarIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="year"
              value={details.year}
              onChange={(e) => handleChange('year', e.target.value)}
              className={`w-full rounded-lg border ${errors.year ? 'border-error' : 'border-border'} bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            >
              <option value="">Select Year</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          {errors.year && (
            <p className="font-caption text-xs text-error mt-1">{errors.year}</p>
          )}
        </div>

        <div>
          <label htmlFor="make" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Make <span className="text-error">*</span>
          </label>
          <div className="relative">
            <Icon 
              name="TruckIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="make"
              value={details.make}
              onChange={(e) => handleChange('make', e.target.value)}
              className={`w-full rounded-lg border ${errors.make ? 'border-error' : 'border-border'} bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            >
              <option value="">Select Make</option>
              {makes.map(make => (
                <option key={make} value={make}>{make}</option>
              ))}
            </select>
          </div>
          {errors.make && (
            <p className="font-caption text-xs text-error mt-1">{errors.make}</p>
          )}
        </div>

        <div>
          <label htmlFor="model" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Model <span className="text-error">*</span>
          </label>
          <div className="relative">
            <Icon 
              name="WrenchScrewdriverIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="model"
              value={details.model}
              onChange={(e) => handleChange('model', e.target.value)}
              disabled={!details.make}
              className={`w-full rounded-lg border ${errors.model ? 'border-error' : 'border-border'} bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <option value="">Select Model</option>
              {getModels().map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
          {errors.model && (
            <p className="font-caption text-xs text-error mt-1">{errors.model}</p>
          )}
        </div>

        <div>
          <label htmlFor="mileage" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
            Mileage (Optional)
          </label>
          <div className="relative">
            <Icon 
              name="ChartBarIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <input
              id="mileage"
              type="text"
              value={details.mileage}
              onChange={(e) => handleChange('mileage', e.target.value.replace(/\D/g, ''))}
              placeholder="e.g., 75000"
              className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            />
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="partDescription" className="font-caption text-xs font-medium text-text-secondary mb-2 block">
          Part Description <span className="text-error">*</span>
        </label>
        <textarea
          id="partDescription"
          value={details.partDescription}
          onChange={(e) => handleChange('partDescription', e.target.value)}
          placeholder="Describe the part you need (e.g., front brake pads, oil filter, air filter, spark plugs)"
          rows={4}
          className={`w-full rounded-lg border ${errors.partDescription ? 'border-error' : 'border-border'} bg-input px-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 resize-none`}
        />
        {errors.partDescription && (
          <p className="font-caption text-xs text-error mt-1">{errors.partDescription}</p>
        )}
        <p className="font-caption text-xs text-muted-foreground mt-2">
          Be as specific as possible for better AI recommendations
        </p>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center space-x-2 rounded-lg bg-primary px-6 py-4 font-body text-base font-medium text-primary-foreground shadow-elevation-md transition-smooth hover:opacity-90 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
            <span>Searching Parts...</span>
          </>
        ) : (
          <>
            <Icon name="MagnifyingGlassIcon" size={20} />
            <span>Find OEM Parts</span>
          </>
        )}
      </button>
    </form>
  );
};

export default VehicleInputForm;